import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import QueryStatsIcon from "@mui/icons-material/QueryStats";

import { GroupAdminInfo } from "../../api/groups";
import { UserAdminInfo } from "../../api/users";
import { useGroupsStore } from "../../store/groupsStore";
import { useDialogState } from "../../hooks/useDialogState";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { GroupStatusDialog } from "./GroupStatusDialog";

interface GroupDetailDialogProps {
  open: boolean;
  group: GroupAdminInfo | undefined;
  onClose: () => void;
}

export const GroupDetailDialog: React.FC<GroupDetailDialogProps> = ({
  open,
  group,
  onClose,
}) => {
  const { groups, removeMember, loadGroupDetail } = useGroupsStore();
  const [removingMemberId, setRemovingMemberId] = React.useState<string>();
  const [membersLoading, setMembersLoading] = React.useState(false);
  const removeConfirm = useDialogState<UserAdminInfo>();
  const statusDialog = useDialogState();
  const isMobile = useIsMobile();
  const { showSnackbar, SnackbarNode } = useSnackbar();

  // The list roster is lightweight (count + name). On open, fetch the enriched
  // group (email + account state) so the member table is complete.
  const groupId = group?.id;
  React.useEffect(() => {
    if (!open || !groupId) return;
    setMembersLoading(true);
    loadGroupDetail(groupId)
      .catch(() => {
        /* keep the lightweight roster already shown */
      })
      .finally(() => setMembersLoading(false));
  }, [open, groupId, loadGroupDetail]);

  // Always reflect latest store data for this group so member changes show immediately
  const currentGroup = groups.find((g) => g.id === group?.id) ?? group;

  if (!currentGroup) return null;

  const handleRemoveMember = async (member: UserAdminInfo) => {
    const memberId = member.fsId ?? member.id;
    setRemovingMemberId(memberId);
    try {
      await removeMember(currentGroup.id, memberId);
      showSnackbar("Member removed.");
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to remove member.",
        "error"
      );
    } finally {
      setRemovingMemberId(undefined);
      removeConfirm.close();
    }
  };

  const memberColumns = [
    {
      id: "displayName",
      label: "Name",
      render: (member: UserAdminInfo) => member.displayName || "—",
    },
    {
      id: "email",
      label: "Email",
      hideOnMobile: true,
    },
    {
      id: "accountState",
      label: "State",
      render: (member: UserAdminInfo) => {
        const isConfirmed = member.accountState === "CONFIRMED";
        return (
          <Chip
            label={member.accountState}
            size="small"
            sx={{
              backgroundColor: isConfirmed ? "success.light" : "error.light",
              color: isConfirmed ? "success.dark" : "error.dark",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      hideOnMobile: true,
      render: (member: UserAdminInfo) => {
        const memberId = member.fsId ?? member.id;
        const isRemoving = removingMemberId === memberId;
        return (
          <Tooltip title="Remove from group">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isRemoving}
                onClick={() => removeConfirm.openWith(member)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {currentGroup.name}
            </Typography>
            <Chip label={`#${currentGroup.tag}`} size="small" />
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Metadata */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2">{currentGroup.description}</Typography>
            {currentGroup.logoUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Logo URL
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {currentGroup.logoUrl}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Members */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Members ({currentGroup.members.length})
          </Typography>

          {membersLoading && currentGroup.members.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : currentGroup.members.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              No members yet.
            </Typography>
          ) : (
            <InfinitePaginationTable
              columns={memberColumns}
              items={currentGroup.members}
              pageSize={10}
              onLoadNext={() => {}}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            startIcon={<QueryStatsIcon />}
            onClick={statusDialog.openDialog}
          >
            View Status
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmActionDialog
        open={removeConfirm.open}
        title="Remove member from group?"
        message={
          <>
            <strong>
              {removeConfirm.data?.displayName || removeConfirm.data?.email}
            </strong>{" "}
            will be removed from <strong>{currentGroup.name}</strong> and lose
            access to the group's shared content.
          </>
        }
        confirmLabel="Remove member"
        loading={removingMemberId !== undefined}
        onClose={removeConfirm.close}
        onConfirm={() => {
          if (removeConfirm.data) handleRemoveMember(removeConfirm.data);
        }}
      />

      <GroupStatusDialog
        open={statusDialog.open}
        groupId={currentGroup.id}
        groupName={currentGroup.name}
        onClose={statusDialog.close}
      />

      {SnackbarNode}
    </>
  );
};
